// The locations an add-Wi-Fi dialog may borrow saved networks FROM.
//
// ── Why this is a choice and not the device's own location ─────────────────
//
// The installer's workflow: a new device sits on the bench at the OFFICE,
// online on the default Smartify network, and it is being prepared for a
// CUSTOMER's house. The network it needs is the customer's — saved on the
// customer's location, with the password — and it has to reach the device over
// MQTT now, while it is still on the bench. The device's own location at that
// moment is the office, or nothing at all, so "the device's location" lists the
// wrong networks or none.
//
// ── Who may see whose list ─────────────────────────────────────────────────
//
// The list carries PASSWORDS, so it is not "every location you can reach":
//   • a Super Admin sees every location on the server, owner-qualified, because
//     they prepare devices for customers they are not mapped to;
//   • everyone else sees only the locations they OWN — `Locations.MasterUserId`
//     is theirs. A guest with room access in a shared house is mapped to the
//     location but does not own it, and gets no list at all (they can still
//     type a network in).
//
// ⚠️ The per-user location reply (`GetLocationsByUserId`) does not carry
// MasterUserId — only the fat `GetAllLocations` row does — so ownership is
// resolved with one `GetLocationDetailsbyLocationID` per mapped location,
// cached per user for the session. A handful of calls, once.
//
// `isAllowed(id)` is the gate every dialog applies BEFORE it lists or registers
// anything for a location — a dialog defaults to the device's own location,
// and the device's location is not necessarily one the user owns.
//
// Shared by the mobile picker, the desktop Wi-Fi tab and the console so the
// three cannot list different sets.

import { useEffect, useState } from 'react';
import { useApp } from '../state/AppContext';
import { getAllLocations, getLocationDetail } from '../api/api';

const rows = (res) => (Array.isArray(res && res.data) ? res.data : []);

/** A label that names the site on a list of a hundred — "Home" alone does not. */
export const locationLabel = (l) => (l
  ? (l.LocationWithName || l.DisplayName || l.LocationName || `Location ${l.LocationID}`)
  : '');

// One resolution per user per session; the owner of a location does not change
// while a dialog is open.
const ownedCache = new Map(); // `${userId}:${ids}` -> Promise<row[]>

/** The subset of `locations` whose MasterUserId is `userId`. */
export async function ownedLocations(userId, locations) {
  const list = locations || [];
  const key = `${userId}:${list.map((l) => l.LocationID).join(',')}`;
  if (!ownedCache.has(key)) {
    ownedCache.set(key, Promise.all(list.map((l) => getLocationDetail(l.LocationID)
      .then((res) => {
        const row = Array.isArray(res.data) ? res.data[0] : res.data;
        return row && Number(row.MasterUserId) === Number(userId) ? l : null;
      })
      .catch(() => null))).then((r) => r.filter(Boolean)));
  }
  return ownedCache.get(key);
}

/** Test seam. */
export const _resetOwnedCache = () => ownedCache.clear();

/**
 * -> { choices: [row], loading, isAllowed(id) }
 * Rows are location rows as the server sends them (LocationID + names).
 * `isAllowed` is false while loading — nothing is listed until it is known.
 */
export default function useLocationChoices() {
  const { isSuperAdmin, userId, locations } = useApp();
  const [choices, setChoices] = useState(null);

  useEffect(() => {
    let alive = true;
    setChoices(null);
    const p = isSuperAdmin ? getAllLocations().then(rows) : ownedLocations(userId, locations);
    p.then((r) => { if (alive) setChoices(r); }).catch(() => { if (alive) setChoices([]); });
    return () => { alive = false; };
  }, [isSuperAdmin, userId, locations]);

  const list = choices || [];
  const isAllowed = (id) => id != null && list.some((l) => String(l.LocationID) === String(id));
  return { choices: list, loading: choices === null, isAllowed };
}
